import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import SelectAreas from "components/ui/selectors/SelectAreas";
import Input from "components/ui/Input";
import useAuth from "hooks/useAuth";
import useGlobalStore from "store/global";
import { useGeneratePendingOrdersReport } from "http-hooks/agorder/useAgorder";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

// Report type options
const reportTypeOptions = [
  { value: "DETAIL", label: "Detailed" },
  { value: "SUMMARY", label: "Summary" }
];

// Scheme order options
const schemeOrderOptions = [
  { value: "0", label: "No" },
  { value: "1", label: "Yes" }
];

// Group options
const groupOptions = [
  { value: "yes", label: "With Group" },
  { value: "no", label: "Without Group" }
];

const PendingOrders = () => {
  const navigate = useNavigate();
  const { user_type, agttp, username } = useAuth();
  const { companyPreferences } = useGlobalStore();
  const { mutateAsync: generateReport, isPending: isGenerating } = useGeneratePendingOrdersReport();

  // State
  const [reportType, setReportType] = useState(null);
  const [isScheme, setIsScheme] = useState({ value: "0", label: "No" });
  const [reportOn, setReportOn] = useState(null);
  const [group, setGroup] = useState({ value: "yes", label: "With Group" });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [orderAsOnDate, setOrderAsOnDate] = useState("");
  const [showItemDetail, setShowItemDetail] = useState(false);

  // Show area filter whenever a customer field is visible
  const showAreaFilter = reportOn?.value === "C" || (reportOn?.value === "V" && agttp === 0);

  const { data: { data: areaCustomers } = {} } = useAllPartySupMasts(
    {
      type: "C",
      level: 0,
      grplevel: 0,
      ag_status: 1,
      ...(selectedArea?.areaId ? { areaId: selectedArea.areaId } : {}),
      noPagination: true,
    },
    showAreaFilter && !!selectedArea
  );

  useEffect(() => {
    if (areaCustomers && areaCustomers.length > 0 && showAreaFilter) {
      setSelectedCustomer(areaCustomers[0]);
    }
  }, [areaCustomers, showAreaFilter]);

  const handleAreaChange = (area) => {
    setSelectedArea(area);
    if (!area) {
      setSelectedCustomer(null);
    }
  };

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set initial date to today
  useEffect(() => {
    if (!orderAsOnDate) {
      setOrderAsOnDate(getCurrentDate());
    }
  }, [orderAsOnDate]);

  // Auto-set reportOn to "C" for customer users (user_type=2)
  useEffect(() => {
    if (user_type === 2 && !reportOn) {
      const customerLabel = agttp === 0 ? "Customer" : "Sales";
      setReportOn({ value: "C", label: customerLabel });
    }
  }, [user_type, agttp, reportOn]);

  // Determine report on options based on user type and agttp
  const reportOnOptions = () => {
    const options = [];
    
    // Add Vendor/Purchases option for admin (user_type 0 or 1)
    if (user_type === 0 || user_type === 1) {
      const vendorLabel = agttp === 0 ? "Vendor" : "Purchases";
      options.push({ value: "V", label: vendorLabel });
    }
    
    // Add Customer/Sales option (always available)
    const customerLabel = agttp === 0 ? "Customer" : "Sales";
    options.push({ value: "C", label: customerLabel });
    
    return options;
  };

  const handleReportTypeChange = (selected) => {
    setReportType(selected);
    // Show item detail checkbox only for SUMMARY
    if (selected?.value !== "SUMMARY") {
      setShowItemDetail(false);
    }
  };

  const handleReportOnChange = (selected) => {
    setReportOn(selected);
    setSelectedVendor(null);
    setSelectedCustomer(null);
    setSelectedArea(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const reportData = {
        reportType: reportType?.value,
        isScheme: isScheme?.value,
        reportOn: reportOn?.value,
        group: group?.value,
        vendor: selectedVendor?.ps_Id || selectedVendor?.value || null,
        customer: selectedCustomer?.ps_Id || selectedCustomer?.value || null,
        orderAsOnDate,
        showItemDetail: reportType?.value === "SUMMARY" ? showItemDetail : false,
      };

      const data = await generateReport(reportData);
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);

      const route = getDocumentViewerRoute("pending-orders", {
        htmlUrl,
        pdfUrl,
        documentNumber: orderAsOnDate,
        documentType: "Pending Orders",
      });

      navigate(route);
    } catch (error) {
      console.error("Failed to generate pending orders report:", error);
    }
  };

  const isFormValid = () => {
    // Per PHP logic: vendor/customer are both optional — leaving blank shows all parties
    return !!(reportType && reportOn && group && orderAsOnDate);
  };

  // Check if scheme orders are allowed
  const allowSchemeOrder = companyPreferences?.allow_scheme_order;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Pending Orders Generator
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate pending orders reports
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              
              {/* Report Type */}
              <div className="sm:col-span-3">
                <Select
                  value={reportType}
                  onChange={handleReportTypeChange}
                  options={reportTypeOptions}
                  label="Report Type"
                  placeholder="Choose here"
                  isRequired={true}
                  isClearable={false}
                />
              </div>

              {/* Scheme Order - only show if company allows it */}
              {allowSchemeOrder ? (
                <div className="sm:col-span-3">
                  <Select
                    value={isScheme}
                    onChange={setIsScheme}
                    options={schemeOrderOptions}
                    label="Scheme Order"
                    placeholder="Choose here"
                    isRequired={true}
                    isClearable={false}
                  />
                </div>
              ) : null}

              {/* Item Detail Checkbox - only show for SUMMARY report type */}
              {reportType?.value === "SUMMARY" && (
                <div className="sm:col-span-3">
                  <Input
                    type="checkbox"
                    label="Show item details in initial load"
                    value={showItemDetail}
                    onChange={(e) => setShowItemDetail(e.target.checked)}
                  />
                </div>
              )}

              {/* Report On - disabled for customer users (user_type=2) */}
              <div className="sm:col-span-3">
                <Select
                  value={reportOn}
                  onChange={handleReportOnChange}
                  options={reportOnOptions()}
                  label="Report On"
                  placeholder="Choose here"
                  isRequired={true}
                  isClearable={false}
                  isDisabled={user_type === 2}
                />
              </div>

              {/* Group */}
              <div className="sm:col-span-3">
                <Select
                  value={group}
                  onChange={setGroup}
                  options={groupOptions}
                  label="Group"
                  placeholder="Choose here"
                  isRequired={true}
                  isClearable={false}
                />
              </div>

              {/* Conditional Fields based on Report On — both optional, blank = show all */}
              {reportOn?.value === "V" && (
                <>
                  <div className="sm:col-span-3">
                    <SelectVendors
                      value={selectedVendor}
                      onChange={setSelectedVendor}
                      label="Vendor (Optional)"
                      placeholder="All Vendors"
                      isClearable={true}
                    />
                  </div>

                  {agttp === 0 && (
                    <>
                      <div className="sm:col-span-3">
                        <SelectAreas
                          value={selectedArea}
                          onChange={handleAreaChange}
                          label="Area (Optional)"
                          placeholder="Select area to auto-fill customer"
                          showAddButton={false}
                          isClearable={true}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <SelectCustomers
                          value={selectedCustomer}
                          onChange={(val) => {
                            setSelectedCustomer(val);
                            if (!val) setSelectedArea(null);
                          }}
                          label="Customer (Optional)"
                          placeholder="All Customers"
                          isClearable={true}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {reportOn?.value === "C" && (
                <>
                  <div className="sm:col-span-3">
                    <SelectAreas
                      value={selectedArea}
                      onChange={handleAreaChange}
                      label="Area (Optional)"
                      placeholder="Select area to auto-fill customer"
                      showAddButton={false}
                      isClearable={true}
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <SelectCustomers
                      value={selectedCustomer}
                      onChange={(val) => {
                        setSelectedCustomer(val);
                        if (!val) setSelectedArea(null);
                      }}
                      label="Customer (Optional)"
                      placeholder="All Customers"
                      isClearable={true}
                    />
                  </div>

                  {agttp === 0 && (
                    <div className="sm:col-span-3">
                      <SelectVendors
                        value={selectedVendor}
                        onChange={setSelectedVendor}
                        label="Vendor (Optional)"
                        placeholder="All Vendors"
                        isClearable={true}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Order As On Date */}
              <div className="sm:col-span-3">
                <Input
                  type="date"
                  value={orderAsOnDate}
                  onChange={(e) => setOrderAsOnDate(e.target.value)}
                  label="Order as on"
                  isRequired={true}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              disabled={!isFormValid() || isGenerating}
              className={`rounded-md px-8 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                isFormValid() && !isGenerating
                  ? "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PendingOrders;

